// 169 R7 — AGGREGATES PER CURRENCY, ONE RULE, ONE CELL.
//
// Every figure that sums DOCUMENTS is the firm-currency sum exactly as it was,
// plus one line per other currency present — rendered HERE and nowhere else,
// so the shape cannot drift between the recap tables, the strip's cards, the
// Clients list's Amount due, the reports and the Costs & Fees table. No sum
// ever adds two currencies: the home figure prints through `fc(home)` (the
// firm's symbol, as today) and each foreign line through `fc(v, code)` (the
// ISO code, R8), at FS.s10, muted, with no colour rule of its own.
//
// A caller with no foreign figure renders the home string alone — the same
// bytes a bare `fc(home)` produced — so the surfaces the 57c freeze photographs
// keep their serialised children wherever no foreign document exists.
import React from "react";
import { fc } from "../utils";
import { FS } from "../lib/tokens";

interface Props {
  readonly home: number | null | undefined;
  /** `{ USD: 400, GBP: 12.5 }` — the other currencies' figures, or nothing. */
  readonly foreign?: Readonly<Record<string, number>> | null;
  /** what the home figure prints when it is not computable (the recap's "—"). */
  readonly empty?: string;
}

export default function MoneyStack({ home, foreign, empty = "—" }: Readonly<Props>) {
  const lines = Object.entries(foreign ?? {}).filter(([, v]) => typeof v === "number" && Number.isFinite(v));
  const homeText = home != null ? fc(home) : empty;
  if (lines.length === 0) return <>{homeText}</>;
  return (
    <>
      {homeText}
      {lines.map(([code, v]) => (
        <div key={code} style={{ fontSize: FS.s10, color: "var(--tm)", fontWeight: 400 }}>
          {fc(v, code)}
        </div>
      ))}
    </>
  );
}

/** A recap's document-basis members, as the server emits them per currency. */
// 191 F.0 — widened to the recap's seven (the Reports tabs ask `foreignOf` by
// RecapMember); a member the block does not carry answers null, as before.
export type ForeignMember = "unmarked" | "unbillable" | "billable" | "invoiced" | "sent" | "paid" | "due" | "invoicedTotal" | "paidTotal" | "outstanding";
export type ForeignColumn = "fo" | "wc" | "vt";

const finite = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);
const memberValue = (block: unknown, member: ForeignMember, col?: ForeignColumn): number | null => {
  if (!block || typeof block !== "object") return null;
  const raw = (block as Record<string, unknown>)[member];
  if (!col) return finite(raw);
  if (!raw || typeof raw !== "object") return null;
  return finite((raw as Record<string, unknown>)[col]);
};

/**
 * The foreign half of a served recap, one member at a time: `foreignOf(recap,
 * "invoiced", "fo")` answers `{ USD: 1000 }`, `foreignOf(recap, "paid")`
 * `{ USD: 790 }` — or null when the recap carries no `foreign` block (the
 * server emits it only when non-empty). Selectors are strings, not functions:
 * the committed lint reads a named parameter inside a function type as unused.
 */
export function foreignOf(
  recap: { foreign?: Readonly<Record<string, unknown>> | null } | null | undefined,
  member: ForeignMember,
  col?: ForeignColumn,
): Record<string, number> | null {
  const f = recap?.foreign;
  if (!f) return null;
  const out: Record<string, number> = {};
  for (const [code, block] of Object.entries(f)) {
    const v = memberValue(block, member, col);
    if (v != null) out[code] = v;
  }
  return Object.keys(out).length ? out : null;
}

/** Σ per currency over rows that each carry a `foreign` block: `sumForeign(rows, "outstanding")`. */
export function sumForeign(
  rows: readonly { foreign?: Readonly<Record<string, unknown>> | null }[],
  member: ForeignMember,
  col?: ForeignColumn,
): Record<string, number> | null {
  const out: Record<string, number> = {};
  for (const row of rows) {
    const f = row.foreign;
    if (!f) continue;
    for (const [code, block] of Object.entries(f)) {
      const v = memberValue(block, member, col);
      if (v != null) out[code] = (out[code] ?? 0) + v;
    }
  }
  return Object.keys(out).length ? out : null;
}
